export interface NotificationOscillatorNode {
  type: OscillatorType;
  frequency: { value: number };
  connect(destination: unknown): void;
  disconnect(): void;
  start(): void;
  stop(): void;
}

export interface NotificationGainNode {
  gain: { value: number };
  connect(destination: unknown): void;
  disconnect(): void;
}

export interface NotificationAudioContext {
  state: AudioContextState;
  destination: unknown;
  createOscillator(): NotificationOscillatorNode;
  createGain(): NotificationGainNode;
  resume(): Promise<void>;
  close(): Promise<void>;
}

export interface NotificationAudioChannel {
  playChime(): Promise<void>;
  startRinging(): Promise<void>;
  stopRinging(): void;
  dispose(): void;
}

interface NotificationAudioChannelOptions {
  createAudioContext?: () => NotificationAudioContext | null;
  setTimeout?: typeof globalThis.setTimeout;
  clearTimeout?: typeof globalThis.clearTimeout;
}

interface ActiveTone {
  oscillator: NotificationOscillatorNode;
  gain: NotificationGainNode;
}

const safelyRun = (action: () => void) => {
  try {
    action();
  } catch {
    return;
  }
};

const defaultAudioContext = (): NotificationAudioContext | null => {
  if (typeof AudioContext === 'undefined') return null;
  return new AudioContext() as NotificationAudioContext;
};

export const createNotificationAudioChannel = (
  options: NotificationAudioChannelOptions = {},
): NotificationAudioChannel => {
  const createAudioContext = options.createAudioContext ?? defaultAudioContext;
  const setTimeoutFn = options.setTimeout ?? globalThis.setTimeout;
  const clearTimeoutFn = options.clearTimeout ?? globalThis.clearTimeout;
  const chimeTimers = new Set<ReturnType<typeof setTimeoutFn>>();
  const ringTimers = new Set<ReturnType<typeof setTimeoutFn>>();
  const chimeTones = new Set<ActiveTone>();
  const ringTones = new Set<ActiveTone>();
  let context: NotificationAudioContext | null = null;
  let ringing = false;
  let disposed = false;

  const clearTimers = (timers: Set<ReturnType<typeof setTimeoutFn>>) => {
    timers.forEach((timer) => clearTimeoutFn(timer));
    timers.clear();
  };

  const schedule = (
    timers: Set<ReturnType<typeof setTimeoutFn>>,
    callback: () => void,
    delay: number,
  ) => {
    const timer = setTimeoutFn(() => {
      timers.delete(timer);
      callback();
    }, delay);
    timers.add(timer);
  };

  const stopTone = (tone: ActiveTone, tones: Set<ActiveTone>) => {
    if (!tones.delete(tone)) return;
    safelyRun(() => tone.oscillator.stop());
    safelyRun(() => tone.oscillator.disconnect());
    safelyRun(() => tone.gain.disconnect());
  };

  const stopTones = (tones: Set<ActiveTone>) => {
    [...tones].forEach((tone) => stopTone(tone, tones));
  };

  const startTone = (
    audioContext: NotificationAudioContext,
    frequency: number,
    tones: Set<ActiveTone>,
  ) => {
    let oscillator: NotificationOscillatorNode | null = null;
    let gain: NotificationGainNode | null = null;
    try {
      oscillator = audioContext.createOscillator();
      gain = audioContext.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gain.gain.value = 0.08;
      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      const tone = { oscillator, gain };
      tones.add(tone);
      oscillator.start();
      return tone;
    } catch {
      safelyRun(() => oscillator?.disconnect());
      safelyRun(() => gain?.disconnect());
      return null;
    }
  };

  const releaseContext = () => {
    const current = context;
    context = null;
    if (!current) return;
    void current.close().catch(() => undefined);
  };

  const ensureContext = async () => {
    if (disposed) return null;
    try {
      context ??= createAudioContext();
      if (!context) return null;
      if (context.state === 'suspended') await context.resume();
      return context;
    } catch {
      clearTimers(chimeTimers);
      clearTimers(ringTimers);
      stopTones(chimeTones);
      stopTones(ringTones);
      releaseContext();
      return null;
    }
  };

  const stopChime = () => {
    clearTimers(chimeTimers);
    stopTones(chimeTones);
  };

  const playChime = async () => {
    if (disposed) return;
    stopChime();
    const audioContext = await ensureContext();
    if (!audioContext || disposed) return;
    const pulse = () => {
      const tone = startTone(audioContext, 880, chimeTones);
      if (tone) schedule(chimeTimers, () => stopTone(tone, chimeTones), 120);
    };
    pulse();
    schedule(chimeTimers, pulse, 220);
  };

  const ringCycle = (audioContext: NotificationAudioContext) => {
    if (!ringing || disposed) return;
    const tones = [
      startTone(audioContext, 440, ringTones),
      startTone(audioContext, 480, ringTones),
    ].filter((tone): tone is ActiveTone => tone != null);
    schedule(
      ringTimers,
      () => {
        tones.forEach((tone) => stopTone(tone, ringTones));
        if (ringing) schedule(ringTimers, () => ringCycle(audioContext), 2_000);
      },
      1_000,
    );
  };

  const startRinging = async () => {
    if (ringing || disposed) return;
    ringing = true;
    const audioContext = await ensureContext();
    if (!audioContext || disposed || !ringing) {
      ringing = false;
      return;
    }
    ringCycle(audioContext);
  };

  const stopRinging = () => {
    ringing = false;
    clearTimers(ringTimers);
    stopTones(ringTones);
  };

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    stopChime();
    stopRinging();
    releaseContext();
  };

  return { playChime, startRinging, stopRinging, dispose };
};
